import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import Badge from '../components/ui/Badge';
import Combobox from '../components/ui/Combobox';
import { Plus, Download, FileText, Trash2, Clock, Send, Printer, SlidersHorizontal, ChevronUp } from 'lucide-react';
import { formatDate } from '../utils/helpers';
import { exportToPDF, exportToExcel } from '../utils/exportUtils';
import { fetchAllPaginatedRows } from '../utils/paginatedExport';
import toast from 'react-hot-toast';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Input from '../components/ui/Input';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/ui/Modal'; // Assuming Modal component exists
import { useSettings } from '../context/SettingsContext';
import { useUIStore } from '../store/useUIStore';

const BulkDispatchModal = ({ isOpen, onClose, selectedCount, onConfirm }) => {
  const [formData, setFormData] = useState({
    modeOfDispatch: 'Post',
    remarks: '',
    priority: 'Normal'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Bulk Dispatch (${selectedCount} items)`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Mode of Dispatch</label>
          <Combobox
            options={[
              { value: "Post", label: "Post" },
              { value: "Courier", label: "Courier" },
              { value: "Hand Delivery", label: "Hand Delivery" },
              { value: "Email", label: "Email" },
              { value: "Fax", label: "Fax" }
            ]}
            value={formData.modeOfDispatch}
            onChange={(val) => setFormData({...formData, modeOfDispatch: val})}
            className="w-full"
            placeholder="Select Mode"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Priority</label>
          <Combobox
            options={[
              { value: "Normal", label: "Normal" },
              { value: "Urgent", label: "Urgent" },
              { value: "Immediate", label: "Immediate" }
            ]}
            value={formData.priority}
            onChange={(val) => setFormData({...formData, priority: val})}
            className="w-full"
            placeholder="Select Priority"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Remarks</label>
          <textarea
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            rows={3}
            value={formData.remarks}
            onChange={(e) => setFormData({...formData, remarks: e.target.value})}
            placeholder="Common remarks for all dispatches..."
          />
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary">Create Dispatches</Button>
        </div>
      </form>
    </Modal>
  );
};

const normalizeDiaryNumber = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const match = text.match(/(\d+)(?!.*\d)/);
  return match ? match[1] : text;
};

const collapseDuplicateCaseRows = (rows = []) => {
  const latestByKey = new Map();
  const keyRegistry = new Map();

  rows.forEach((row) => {
    const rowType = String(row?.type || row?.letterType || 'receipt').trim().toLowerCase();
    const diaryNumber = String(
      row?.diaryNumber ||
      row?.dakNumber ||
      row?.receiptNumber ||
      row?.dispatchNumber ||
      row?.fileNumber ||
      ''
    ).trim();
    const subject = String(row?.subject || row?.actionRequired || '').trim().toLowerCase();
    const sender = String(row?.sender || row?.forwardedToName || row?.assignedToUser?.name || row?.recipient || '').trim().toLowerCase();
    const dateKey = String(row?.dateReceived || row?.createdAt || row?.updatedAt || '').slice(0, 10);
    const diaryKey = diaryNumber ? `${rowType}::diary::${diaryNumber.toLowerCase()}` : null;
    const fallbackKey = `${rowType}::fallback::${subject || 'no-subject'}::${sender || 'no-sender'}::${dateKey || 'no-date'}`;
    const candidateKeys = diaryKey ? [diaryKey, fallbackKey] : [fallbackKey];

    const mappedCanonicalKey = candidateKeys.map((key) => keyRegistry.get(key)).find((key) => key !== undefined);
    const canonicalKey = mappedCanonicalKey || candidateKeys[0];

    for (const key of candidateKeys) {
      keyRegistry.set(key, canonicalKey);
    }

    const current = latestByKey.get(canonicalKey);
    if (!current) {
      latestByKey.set(canonicalKey, row);
      return;
    }

    const currentDate = new Date(current?.updatedAt || current?.createdAt || 0).getTime();
    const incomingDate = new Date(row?.updatedAt || row?.createdAt || 0).getTime();

    if (incomingDate > currentDate) {
      latestByKey.set(canonicalKey, row);
    }
  });

  return Array.from(latestByKey.values());
};

const getReversalDate = (row) => {
  const direct = row?.dateOfReversal || row?.reversalDate || row?.reversedAt || '';
  if (direct) return direct;
  const actionText = String(row?.actionTaken || row?.lastAction || '').toLowerCase();
  if (['reverted', 'withdrawn', 'returned', 'reversal'].some(term => actionText.includes(term))) {
    return row?.updatedAt || row?.createdAt || '';
  }
  return '';
};

const ReceiptList = () => {
  const { user } = useAuth();
  const { settings } = useSettings();
  const { tableDensity, setTableDensity } = useUIStore();
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [visibleReceiptColumns, setVisibleReceiptColumns] = useState([]);
  const [tableReceiptColumnWidths, setTableReceiptColumnWidths] = useState({});
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    diaryNumber: '',
    subject: '',
    dateReceived: '',
    sender: '',
    possession: '',
    documentType: '',
    priority: '',
    remarks: ''
  });
  const [selectedReceipts, setSelectedReceipts] = useState(new Set());
  const [showBulkDispatch, setShowBulkDispatch] = useState(false);
  
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: () => {}
  });
  const { get, post, del } = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchReceipts();
  }, [page, debouncedSearch, sortConfig, dateRange]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const { from, to } = dateRange;
      const response = await get(`/receipt?page=${page}&limit=10&search=${debouncedSearch}&sortBy=${sortConfig.key}&order=${sortConfig.direction.toUpperCase()}&dateFrom=${from}&dateTo=${to}`);
      const records = Array.isArray(response?.data) ? collapseDuplicateCaseRows(response.data) : [];
      setReceipts(records);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch diary records', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const columns = [
    { 
      header: 'Diary No.', 
      accessorKey: 'diaryNumber',
      className: 'w-40',
      cellClassName: 'w-40',
      render: (row) => (
        <span className="font-medium text-gray-900">{row.diaryNumber || row.receiptNumber}</span>
      ),
      sortable: true 
    },
    {
      header: 'Subject',
      accessorKey: 'subject',
      className: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]',
      cellClassName: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]',
      noWrap: false,
      sortable: true,
      render: (row) => (
        <span className="block leading-snug break-words [overflow-wrap:anywhere]" title={row.subject || ''}>
          {row.subject || '-'}
        </span>
      )
    },
    { header: 'Date', accessorKey: 'dateReceived', className: 'w-32', cellClassName: 'w-32', render: (row) => formatDate(row.dateReceived), sortable: true },
    { header: 'Sender', accessorKey: 'sender', className: 'w-44', cellClassName: 'w-44', sortable: true },
    {
      header: 'Remarks',
      accessorKey: 'remarks',
      className: 'hidden 2xl:table-cell 2xl:w-[18rem]',
      cellClassName: 'hidden 2xl:table-cell 2xl:w-[18rem]',
      noWrap: false,
      render: (row) => (
        <span className="block text-sm text-gray-600 leading-snug break-words [overflow-wrap:anywhere]" title={row.remarks || ''}>
          {row.remarks || '-'}
        </span>
      )
    },
    { 
      header: 'Possession', 
      accessorKey: 'forwardedToName', 
      className: 'hidden lg:table-cell w-44',
      cellClassName: 'hidden lg:table-cell w-44',
      render: (row) => row.forwardedToName || row.createdByName || 'Central Registry',
      sortable: true 
    },
    { header: 'Priority', accessorKey: 'priority', className: 'w-28', cellClassName: 'w-28', render: (row) => <Badge priority={row.priority}>{row.priority}</Badge>, sortable: true },
    { 
      header: 'Actions', 
      accessorKey: 'actions', 
      className: 'w-36',
      cellClassName: 'w-36',
      render: (row) => (
        <div className="flex items-center gap-2">
          {isEfileEnabled && (
            <Button 
              variant="ghost" 
              className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 p-1 h-auto"
              title="Digital Green Sheet"
              onClick={(e) => { 
                e.stopPropagation(); 
                navigate(`/green-sheet/${row.id}`); 
              }}
            >
              <FileText className="w-4 h-4" />
            </Button>
          )}
          <Button variant="link" onClick={(e) => { e.stopPropagation(); navigate(`/diary/${row.id}/view`); }}>
            View
          </Button>
          <Button 
            variant="ghost" 
            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 h-auto"
            onClick={(e) => { 
              e.stopPropagation(); 
              handleDeleteClick(row); 
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    },
  ];

  const filteredReceipts = (Array.isArray(receipts) ? receipts : []).filter((row) => {
    const q = (v) => String(v || '').trim().toLowerCase();
    const includes = (value, term) => {
      const t = q(term);
      if (!t) return true;
      return q(value).includes(t);
    };

    const dateQ = String(columnFilters.dateReceived || '').trim();
    const dateV = row?.dateReceived ? String(row.dateReceived).substring(0, 10) : '';
    const possessionValue = row?.forwardedToName || row?.createdByName || 'Central Registry';

    return (
      includes(row?.diaryNumber || row?.receiptNumber, columnFilters.diaryNumber) &&
      includes(row?.subject, columnFilters.subject) &&
      includes(row?.sender, columnFilters.sender) &&
      includes(row?.remarks, columnFilters.remarks) &&
      includes(possessionValue, columnFilters.possession) &&
      includes(row?.documentType || 'Letter', columnFilters.documentType) &&
      includes(row?.priority, columnFilters.priority) &&
      (!dateQ || dateV.includes(dateQ))
    );
  });

  const applyColumnFilters = (rows) => (Array.isArray(rows) ? rows : []).filter((row) => {
    const q = (v) => String(v || '').trim().toLowerCase();
    const includes = (value, term) => {
      const t = q(term);
      if (!t) return true;
      return q(value).includes(t);
    };

    const dateQ = String(columnFilters.dateReceived || '').trim();
    const dateV = row?.dateReceived ? String(row.dateReceived).substring(0, 10) : '';
    const possessionValue = row?.forwardedToName || row?.createdByName || 'Central Registry';

    return (
      includes(row?.diaryNumber || row?.receiptNumber, columnFilters.diaryNumber) &&
      includes(row?.subject, columnFilters.subject) &&
      includes(row?.sender, columnFilters.sender) &&
      includes(row?.remarks, columnFilters.remarks) &&
      includes(possessionValue, columnFilters.possession) &&
      includes(row?.documentType || 'Letter', columnFilters.documentType) &&
      includes(row?.priority, columnFilters.priority) &&
      (!dateQ || dateV.includes(dateQ))
    );
  });

  const fetchAllReceiptsForExport = async () => {
    const { from, to } = dateRange;
    const allRows = await fetchAllPaginatedRows({
      fetchPage: (currentPage) => get(`/receipt?page=${currentPage}&limit=200&search=${debouncedSearch}&sortBy=${sortConfig.key}&order=${sortConfig.direction.toUpperCase()}&dateFrom=${from}&dateTo=${to}`)
    });

    const dedupedRows = collapseDuplicateCaseRows(allRows);
    const rows = applyColumnFilters(dedupedRows);
    const enriched = await Promise.all(rows.map(async (row) => {
      let reversalDate = getReversalDate(row);
      if (!reversalDate) {
        try {
          const logRes = await get(`/compliance/logs?letterType=receipt&letterId=${row.id}`);
          const logs = Array.isArray(logRes?.data) ? logRes.data : [];
          const reversalLog = logs.find((log) => {
            const action = String(log?.actionTaken || '').toLowerCase();
            return ['reverted', 'withdrawn', 'returned', 'reversal'].some(term => action.includes(term));
          });
          reversalDate = reversalLog?.updatedAt || '';
        } catch {
          reversalDate = '';
        }
      }

      let linkedCasesSummary = '-';
      try {
        const relationRes = await get(`/relations/receipt/${row.id}`);
        const relations = Array.isArray(relationRes?.data) ? relationRes.data : [];
        const visibleRelations = relations.filter((rel) => rel && (rel.type === 'receipt' || rel.type === 'dispatch' || rel.type === 'efile'));
        linkedCasesSummary = visibleRelations.length > 0
          ? visibleRelations.slice(0, 8).map((rel) => `${String(rel?.type || 'Case').toUpperCase()} ${String(rel?.number || rel?.id || '')}${rel?.relationType ? ` (${rel.relationType})` : ''}`).join(' | ')
          : '-';
      } catch {
        linkedCasesSummary = '-';
      }

      return {
        ...row,
        diarySerial: normalizeDiaryNumber(row.diaryNumber || row.receiptNumber || row.id),
        forwardTo: row.forwardedToName || row.createdByName || 'Central Registry',
        forwardDate: row.forwardedAt || row.updatedAt || '',
        reversalDate,
        linkedCasesSummary,
        referenceNumber: row.referenceNumber || '-',
        documentType: row.documentType || 'Letter',
        remarks: row.remarks || '-',
        dateOfLetter: row.dateOfLetter || row.dateReceived || ''
      };
    }));

    return enriched;
  };

  const handleBulkDispatch = async (formData) => {
    try {
      await post('/dispatch/bulk', {
        receiptIds: Array.from(selectedReceipts),
        ...formData
      });
      toast.success('Bulk dispatch created successfully');
      setShowBulkDispatch(false);
      setSelectedReceipts(new Set());
      fetchReceipts();
    } catch (error) {
      console.error('Failed to create bulk dispatch', error);
      toast.error('Failed to create bulk dispatch');
    }
  };

  const handleDeleteClick = (row) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Diary Record',
      message: `Are you sure you want to delete diary record ${row.receiptNumber}? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => deleteReceipt(row.id)
    });
  };

  const deleteReceipt = async (id) => {
    try {
      await del(`/receipt/${id}`);
      toast.success('Diary record deleted successfully');
      fetchReceipts();
    } catch (error) {
      console.error('Failed to delete diary record', error);
      toast.error('Failed to delete diary record');
    }
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const getVisibleColumns = (list = []) => {
    const key = (column) => String(column.header || column.accessorKey || '').trim();
    return list.filter((column) => {
      const match = visibleReceiptColumns.find((item) => item.key === key(column));
      return match ? match.visible : column.visible !== false;
    });
  };

  const getExportColumnWidths = (exportColumns = []) => {
    const widths = {};
    exportColumns.forEach((column, index) => {
      const key = String(column.header || column.accessorKey || '').trim();
      const width = Number(tableReceiptColumnWidths[key] || 0);
      if (width > 0) {
        widths[index] = { wch: Math.min(Math.max(width / 8, 12), 40) };
      }
    });
    return widths;
  };

  const handleExportPDF = async () => {
    try {
      const rows = await fetchAllReceiptsForExport();
      const exportColumns = [
        { header: 'Diary #', accessorKey: row => normalizeDiaryNumber(row.diaryNumber || row.receiptNumber || row.id) },
        { header: 'Date of Letter', accessorKey: row => row.dateOfLetter ? formatDate(row.dateOfLetter) : (row.dateReceived ? formatDate(row.dateReceived) : '-') },
        { header: 'Reference #', accessorKey: row => row.referenceNumber || '-' },
        { header: 'Document Type', accessorKey: row => row.documentType || 'Letter' },
        { header: 'Subject', accessorKey: row => row.subject || '-' },
        { header: 'Linked Cases', accessorKey: row => row.linkedCasesSummary || '-' },
        { header: 'Forward To', accessorKey: row => row.forwardTo || 'Central Registry' },
        { header: 'Forward Date', accessorKey: row => row.forwardDate ? formatDate(row.forwardDate) : '-' },
        { header: 'Remarks', accessorKey: row => row.remarks || '-' },
        { header: 'Date of Reversal', accessorKey: row => row.reversalDate ? formatDate(row.reversalDate) : '-' }
      ];

      await exportToPDF(rows, getVisibleColumns(exportColumns), 'Diary Register', {
        startDate: dateRange.from,
        endDate: dateRange.to,
        orientation: 'landscape',
        pageSize: 'legal',
        officeName: user?.officeName || '',
        columnWidths: getExportColumnWidths(getVisibleColumns(exportColumns))
      });
      toast.success('PDF exported successfully');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handlePrintPDF = async () => {
    try {
      const rows = await fetchAllReceiptsForExport();
      const exportColumns = [
        { header: 'Diary #', accessorKey: row => normalizeDiaryNumber(row.diaryNumber || row.receiptNumber || row.id) },
        { header: 'Date of Letter', accessorKey: row => row.dateOfLetter ? formatDate(row.dateOfLetter) : (row.dateReceived ? formatDate(row.dateReceived) : '-') },
        { header: 'Reference #', accessorKey: row => row.referenceNumber || '-' },
        { header: 'Document Type', accessorKey: row => row.documentType || 'Letter' },
        { header: 'Subject', accessorKey: row => row.subject || '-' },
        { header: 'Linked Cases', accessorKey: row => row.linkedCasesSummary || '-' },
        { header: 'Forward To', accessorKey: row => row.forwardTo || 'Central Registry' },
        { header: 'Forward Date', accessorKey: row => row.forwardDate ? formatDate(row.forwardDate) : '-' },
        { header: 'Remarks', accessorKey: row => row.remarks || '-' },
        { header: 'Date of Reversal', accessorKey: row => row.reversalDate ? formatDate(row.reversalDate) : '-' }
      ];

      const result = await exportToPDF(rows, getVisibleColumns(exportColumns), 'Diary Register', {
        startDate: dateRange.from,
        endDate: dateRange.to,
        orientation: 'landscape',
        pageSize: 'legal',
        officeName: user?.officeName || '',
        output: 'print',
        columnWidths: getExportColumnWidths(getVisibleColumns(exportColumns))
      });
      if (result?.mode === 'print-fallback-download') {
        toast.success('Popup blocked. PDF downloaded instead.');
      } else {
        toast.success('Print preview opened');
      }
    } catch {
      toast.error('Failed to print');
    }
  };

  const handleExportExcel = async () => {
    const rows = await fetchAllReceiptsForExport();
    const exportColumns = [
      { header: 'Diary #', accessorKey: row => normalizeDiaryNumber(row.diaryNumber || row.receiptNumber || row.id) },
      { header: 'Date of Letter', accessorKey: row => row.dateOfLetter ? formatDate(row.dateOfLetter) : (row.dateReceived ? formatDate(row.dateReceived) : '-') },
      { header: 'Reference #', accessorKey: row => row.referenceNumber || '-' },
      { header: 'Document Type', accessorKey: row => row.documentType || 'Letter' },
      { header: 'Subject', accessorKey: row => row.subject || '-' },
      { header: 'Linked Cases', accessorKey: row => row.linkedCasesSummary || '-' },
      { header: 'Forward To', accessorKey: row => row.forwardTo || 'Central Registry' },
      { header: 'Forward Date', accessorKey: row => row.forwardDate ? formatDate(row.forwardDate) : '-' },
      { header: 'Remarks', accessorKey: row => row.remarks || '-' },
      { header: 'Date of Reversal', accessorKey: row => row.reversalDate ? formatDate(row.reversalDate) : '-' }
    ];
    const workbookData = rows.map((row) => Object.fromEntries(getVisibleColumns(exportColumns).map((column) => {
      const value = typeof column.accessorKey === 'function' ? column.accessorKey(row) : (row[column.accessorKey] ?? '');
      return [column.header, value];
    })));
    exportToExcel(workbookData, 'Diary_Register', {
      columnWidths: getExportColumnWidths(getVisibleColumns(exportColumns))
    });
    toast.success('Excel exported successfully');
  };

  return (
    <div className="space-y-5">
      <div className="rounded-[28px] border border-emerald-100 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-4 text-white shadow-[0_18px_40px_rgba(16,185,129,0.22)] sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-100">Diary workflow</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Diary Register</h1>
            <p className="mt-1 text-sm text-emerald-100/90">Manage incoming correspondence, case movement, and office tracking.</p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <div className="inline-flex rounded-xl border border-white/15 bg-white/10 p-1">
              <Button
                size="sm"
                variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
                onClick={() => setTableDensity('comfortable')}
                className={tableDensity === 'comfortable' ? 'bg-white text-emerald-700 hover:bg-emerald-50' : 'text-white hover:bg-white/10'}
              >
                Comfortable
              </Button>
              <Button
                size="sm"
                variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
                onClick={() => setTableDensity('compact')}
                className={tableDensity === 'compact' ? 'bg-white text-emerald-700 hover:bg-emerald-50' : 'text-white hover:bg-white/10'}
              >
                Compact
              </Button>
            </div>
            <Button variant="secondary" onClick={handleExportPDF} className="bg-white/10 text-white border-white/15 hover:bg-white/15">
              <FileText className="w-4 h-4 mr-2 text-red-200" /> PDF
            </Button>
            <Button variant="secondary" onClick={handlePrintPDF} className="bg-white/10 text-white border-white/15 hover:bg-white/15">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="secondary" onClick={handleExportExcel} className="bg-white/10 text-white border-white/15 hover:bg-white/15">
              <Download className="w-4 h-4 mr-2 text-green-200" /> Excel
            </Button>
            <Button variant="primary" onClick={() => navigate('/diary/new')} className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-sm">
              <Plus className="w-5 h-5 mr-2" />
              New Diary
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Total items</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{receipts.length}</div>
          <div className="mt-1 text-xs text-slate-500">Current page volume</div>
        </div>
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Received today</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{receipts.filter(item => item.dateReceived && item.dateReceived.slice(0,10) === new Date().toISOString().slice(0,10)).length}</div>
          <div className="mt-1 text-xs text-slate-500">Live daily count</div>
        </div>
        <div className="glass-card p-4">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Urgent files</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{receipts.filter(item => String(item.priority || '').toLowerCase() === 'urgent').length}</div>
          <div className="mt-1 text-xs text-slate-500">Priority lane</div>
        </div>
      </div>

      <div className="page-section p-3 sm:p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex-1 w-full">
            <SearchInput 
              value={search} 
              onChange={setSearch} 
              onClear={() => setSearch('')}
              placeholder="Search by diary number, subject, or sender..." 
              className="border-slate-200 focus:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:w-auto">
             <div className="w-full md:w-40">
              <Input 
                type="date" 
                label="From"
                value={dateRange.from} 
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="mb-0 border-slate-200 focus:ring-indigo-500"
              />
            </div>
            <div className="w-full md:w-40">
              <Input 
                type="date" 
                label="To"
                value={dateRange.to} 
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="mb-0 border-slate-200 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="page-section p-3 sm:p-4">
        <div className="flex justify-end mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMoreFilters(prev => !prev)}
            className="inline-flex items-center gap-2 rounded-full border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
          >
            {showMoreFilters ? <ChevronUp className="h-3.5 w-3.5" /> : <SlidersHorizontal className="h-3.5 w-3.5" />}
            {showMoreFilters ? 'Hide Filters' : 'More Filters'}
          </Button>
        </div>

        {showMoreFilters && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-8">
            <Input label="Diary No." value={columnFilters.diaryNumber} onChange={(e) => setColumnFilters(p => ({ ...p, diaryNumber: e.target.value }))} />
            <Input label="Subject" value={columnFilters.subject} onChange={(e) => setColumnFilters(p => ({ ...p, subject: e.target.value }))} />
            <Input label="Date" value={columnFilters.dateReceived} onChange={(e) => setColumnFilters(p => ({ ...p, dateReceived: e.target.value }))} />
            <Input label="Sender" value={columnFilters.sender} onChange={(e) => setColumnFilters(p => ({ ...p, sender: e.target.value }))} />
            <Input label="Remarks" value={columnFilters.remarks} onChange={(e) => setColumnFilters(p => ({ ...p, remarks: e.target.value }))} />
            <Input label="Possession" value={columnFilters.possession} onChange={(e) => setColumnFilters(p => ({ ...p, possession: e.target.value }))} />
            <Input label="Doc Type" value={columnFilters.documentType} onChange={(e) => setColumnFilters(p => ({ ...p, documentType: e.target.value }))} />
            <Input label="Priority" value={columnFilters.priority} onChange={(e) => setColumnFilters(p => ({ ...p, priority: e.target.value }))} />
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.06)]">
        <Table 
          columns={columns} 
          data={filteredReceipts} 
          onRowClick={(row) => navigate(`/diary/${row.id}/view`)}
          sortConfig={sortConfig}
          onSort={handleSort}
          onRowSelect={setSelectedReceipts}
          selectedRows={selectedReceipts}
          tableClassName="table-fixed"
          density={tableDensity}
          enableColumnVisibility
          enableColumnResize
          showColumnControls
          onColumnVisibilityChange={setVisibleReceiptColumns}
          onColumnResizeChange={setTableReceiptColumnWidths}
        />

        <Pagination 
          currentPage={page} 
          totalPages={totalPages} 
          onPageChange={setPage} 
          totalItems={filteredReceipts.length}
          itemsPerPage={10}
        />
      </div>

      <BulkDispatchModal 
        isOpen={showBulkDispatch}
        onClose={() => setShowBulkDispatch(false)}
        selectedCount={selectedReceipts.size}
        onConfirm={handleBulkDispatch}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />
    </div>
  );
};

export default ReceiptList;
